import React, { useEffect, useMemo } from 'react';

import { useFormikContext } from 'formik';

import ChartTitleField from '@components/experiments/plotDisplay/fields/ChartTitleField';
import FullWidthToggleField from '@components/experiments/plotDisplay/fields/FullWidthToggleField';
import ThemeFieldGroup from '@components/experiments/plotDisplay/groups/ThemeFieldGroup';
import CheckboxField from '@components/forms/CheckboxField';

import usePlot from '@hooks/usePlot';

import {
    functionalAnnotationAnalysisHeaderMapping,
    PlotDataHeaderAnalysis,
} from '@models/analysis/PlotDataHeaderAnalysis';
import Experiment from '@models/Experiment';
import { GenericCellData } from '@models/ExperimentData';
import Plot from '@models/Plot';
import { PeakSetConfig } from '@models/PlotConfigs';

import { capitalizeWordsAndReplaceUnderscores } from '@util/StringUtil';

import DisplayUnitsSelectField from '../fields/DisplayUnitsSelectField';
import PeakSetSelectField from '../fields/PeakSetSelectField';
import LegendFieldGroup from '../groups/LegendFieldGroup';

type Props = { plot: Plot; experiment: Experiment };
const PieChartDisplayFields = ({ plot, experiment }: Props) => {
    const analysis = plot.analysis as PlotDataHeaderAnalysis | null;
    const { values, setFieldValue } = useFormikContext<PeakSetConfig>();
    const { plotData } = usePlot({
        experiment,
        plot,
        plotId: plot.uuid,
    });
    const plotDataHeaders = analysis?.plot_data_headers ?? [];
    const identifier = plotDataHeaders?.[0] ?? 'peak_set';
    const isFunctionalAnnotationAnalysis = plot.analysis_type === 'functional_annotation';

    const valueToDisplayOptions = useMemo(() => {
        const rawItems = (plotData?.items as GenericCellData[]) ?? [];
        const mappedOptions: { value: string; label: string }[] = [];

        for (const item of rawItems) {
            const rawValue = item[identifier];
            let stringValue: string;
            let displayLabel: string;

            if (rawValue === null || rawValue === undefined) {
                continue; // Skip items with null or undefined values
            } else if (typeof rawValue === 'boolean') {
                stringValue = String(rawValue); // "true" or "false"
                displayLabel = capitalizeWordsAndReplaceUnderscores(stringValue);
            } else if (typeof rawValue === 'number') {
                stringValue = String(rawValue);
                displayLabel = capitalizeWordsAndReplaceUnderscores(stringValue);
            } else {
                // Assuming it's a string, if not already handled
                stringValue = String(rawValue);
                displayLabel = capitalizeWordsAndReplaceUnderscores(String(rawValue));
            }
            mappedOptions.push({ value: stringValue, label: displayLabel });
        }

        // Ensure unique values by converting to a Map and back to an array
        // This keeps the last occurrence of each unique value based on the stringValue
        const uniqueOptionsMap = new Map<string, { value: string; label: string }>();
        for (const opt of mappedOptions) {
            uniqueOptionsMap.set(opt.value, opt);
        }
        return Array.from(uniqueOptionsMap.values());
    }, [plotData, identifier]);

    useEffect(() => {
        // Only set the default if peak_set is not already set and options are available
        if (!values.peak_set && valueToDisplayOptions.length > 0 && valueToDisplayOptions[0]?.value) {
            setFieldValue('peak_set', valueToDisplayOptions[0].value);
            if (!values.plot_title) {
                setFieldValue('plot_title', valueToDisplayOptions[0].label);
            }
        }
    }, [values.peak_set, values.plot_title, valueToDisplayOptions]);

    return (
        <>
            <FullWidthToggleField />
            <ChartTitleField
                placeholder={plot.analysis?.name}
                onChange={(text) => {
                    if (!text && values.peak_set) {
                        setFieldValue('plot_title', values.peak_set);
                    }
                }}
            />
            <PeakSetSelectField options={valueToDisplayOptions} />
            {plot?.analysis_type === 'assay_summary_discrete' ? <DisplayUnitsSelectField /> : null}
            <div className="mb-8">
                <CheckboxField
                    className="flex items-center"
                    offsetCheckbox={false}
                    name="show_percentages"
                    label="Show value on pie chart"
                />
            </div>
            <ThemeFieldGroup hideStyle />
            <LegendFieldGroup
                isSortable
                description="Click and drag the legend items to reorder. Plot will only show features included in the analysis"
                loading={!plotDataHeaders?.length}
                items={
                    plotDataHeaders?.slice(1)?.map((id) => ({
                        id,
                        display_name: isFunctionalAnnotationAnalysis
                            ? functionalAnnotationAnalysisHeaderMapping[
                                  id as keyof typeof functionalAnnotationAnalysisHeaderMapping
                              ]
                            : capitalizeWordsAndReplaceUnderscores(id),
                    })) ?? []
                }
                legendPopoverTitle="Feature display name"
            />
        </>
    );
};

export default PieChartDisplayFields;
